import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import ErrorBoundary from './ErrorBoundary';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signInAnonymously, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { getFirestore, doc, setDoc } from 'firebase/firestore';

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyAXkH6ZSo87di1_7QMU9xAZG02GKUMVis8",
  authDomain: "blooddonationreact-5995a.firebaseapp.com",
  projectId: "blooddonationreact-5995a",
  storageBucket: "blooddonationreact-5995a.appspot.com",
  messagingSenderId: "355369527417",
  appId: "1:355369527417:web:c60b0a3c33456f4dbc0ca8",
  measurementId: "G-4FPN7Z7NRF"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

function BloodLinkApp() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'signup'
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard', 'search', 'donate', 'profile'
  const [notification, setNotification] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);

  // Show notification helper
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Simulate progress for form submissions
  const simulateProgress = () => {
    setProgress(0);
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 100);
  };

  useEffect(() => {
    console.log("Setting up Firebase auth...");
    try {
      const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
        console.log("Auth state changed:", currentUser ? "User signed in" : "No user");
        setUser(currentUser);
        setLoading(false);
      });

      return () => unsubscribe();
    } catch (err) {
      console.error("Firebase setup error:", err);
      setError(err.message);
      setLoading(false);
    }
  }, []);

  const handleLogin = async (email, password) => {
    setIsSubmitting(true);
    simulateProgress();
    try {
      await signInWithEmailAndPassword(auth, email, password);
      setError(null);
      showNotification('Successfully logged in! Welcome back! 🎉');
    } catch (err) {
      setError(err.message);
      showNotification('Login failed. Please check your credentials.', 'error');
    } finally {
      setIsSubmitting(false);
      setProgress(0);
    }
  };

  const handleSignup = async (formData) => {
    setIsSubmitting(true);
    simulateProgress();
    try {
      // Create user account
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const user = userCredential.user;
      
      // Store additional user profile data in Firestore
      await setDoc(doc(db, 'users', user.uid), {
        fullName: formData.fullName,
        email: formData.email,
        bloodType: formData.bloodType,
        contact: formData.contact,
        address: formData.addressString || formData.address,
        addressComponents: formData.addressComponents || {
          street: formData.address.street || '',
          city: formData.address.city || '',
          state: formData.address.state || '',
          pinCode: formData.address.pinCode || ''
        },
        isDonor: true,
        createdAt: new Date(),
        lastDonation: null,
        location: {
          city: formData.addressComponents?.city || formData.address.city || '',
          state: formData.addressComponents?.state || formData.address.state || '',
          pinCode: formData.addressComponents?.pinCode || formData.address.pinCode || ''
        }
      });
      
      setError(null);
      showNotification('Account created successfully! Welcome to BloodLink! 🩸');
    } catch (err) {
      setError(err.message);
      showNotification('Signup failed. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
      setProgress(0);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      showNotification('Logged out successfully. See you soon! 👋');
    } catch (err) {
      setError(err.message);
      showNotification('Logout failed. Please try again.', 'error');
    }
  };

  const handleAnonymousSignIn = async () => {
    setIsSubmitting(true);
    try {
      await signInAnonymously(auth);
      showNotification('Signed in as guest! Limited features available.');
    } catch (err) {
      setError(err.message);
      showNotification('Guest signin failed. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (error) {
    return (
      <div style={{
        color: '#dc2626', 
        padding: '40px', 
        maxWidth: '600px', 
        margin: '50px auto', 
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '20px',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
        textAlign: 'center'
      }}>
        <h2 style={{marginBottom: '20px', fontSize: '28px'}}>🔥 Firebase Error</h2>
        <p style={{fontSize: '16px', lineHeight: '1.6'}}>{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          style={{marginTop: '20px', background: 'linear-gradient(135deg, #dc2626, #ef4444)'}}
        >
          Reload Application
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 50%, #cbd5e1 100%)',
        color: '#334155'
      }}>
        <div className="loading-spinner"></div>
        <h2 style={{marginTop: '20px', fontSize: '28px', fontWeight: '600', color: '#475569'}}>
          🩸 Loading BloodLink
        </h2>
        <p style={{fontSize: '16px', opacity: '0.7', marginTop: '10px', color: '#64748b'}}>
          Connecting with Firebase...
        </p>
      </div>
    );
  }

  // Show authentication forms for users who aren't signed in
  if (!user) {
    return (
      <>
        {notification && (
          <div className={`notification ${notification.type === 'error' ? 'error' : ''}`}>
            {notification.message}
          </div>
        )}
        <div className="auth-container">
          <h1 style={{
            background: 'linear-gradient(135deg, #dc2626, #ef4444, #f87171)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textAlign: 'center', 
            marginBottom: '40px',
            fontSize: '48px',
            fontWeight: '900',
            letterSpacing: '-1px',
            textShadow: '0 4px 8px rgba(220, 38, 38, 0.3)'
          }}>
            🩸 BloodLink
          </h1>
          
          {isSubmitting && (
            <div className="progress-bar">
              <div className="progress-fill" style={{width: `${progress}%`}}></div>
            </div>
          )}
        
          <div className="auth-tabs" style={{display: 'flex', marginBottom: '30px'}}>
            <button 
              onClick={() => setAuthMode('login')}
              style={{
                flex: 1,
                padding: '12px 16px',
                backgroundColor: authMode === 'login' ? 'rgba(220, 38, 38, 0.9)' : 'rgba(243, 244, 246, 0.9)',
                color: authMode === 'login' ? 'white' : '#374151',
                border: 'none',
                borderRadius: '12px 0 0 12px',
                cursor: 'pointer',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}
            >
              Login
            </button>
            <button 
              onClick={() => setAuthMode('signup')}
              style={{
                flex: 1,
                padding: '12px 16px',
                backgroundColor: authMode === 'signup' ? 'rgba(220, 38, 38, 0.9)' : 'rgba(243, 244, 246, 0.9)',
                color: authMode === 'signup' ? 'white' : '#374151',
                border: 'none',
                borderRadius: '0 12px 12px 0',
                cursor: 'pointer',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}
            >
              Sign Up
            </button>
          </div>

        {authMode === 'login' ? (
          <LoginForm onLogin={handleLogin} />
        ) : (
          <SignupForm onSignup={handleSignup} />
        )}

        {error && (
          <div style={{color: 'red', marginTop: '15px', fontSize: '14px'}}>
            {error}
          </div>
        )}

        <button 
          onClick={handleAnonymousSignIn}
          style={{
            width: '100%',
            marginTop: '15px',
            padding: '10px',
            backgroundColor: '#6b7280',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          Continue as Guest
        </button>
        </div>
      </>
    );
  }

  return (
    <>
      {notification && (
        <div className={`notification ${notification.type === 'error' ? 'error' : ''}`}>
          {notification.message}
        </div>
      )}
      <div className="main-container">
        <div className="header-flex" style={{marginBottom: '40px'}}>
          <h1 className="app-title">🩸 BloodLink</h1>
          {user ? (
            <div className="user-info">
              <span style={{
                marginRight: '20px',
                padding: '8px 16px',
                background: 'rgba(255, 255, 255, 0.9)',
                borderRadius: '12px',
                fontWeight: '600',
                color: '#374151'
              }}>
                {user.email || `Guest ${user.uid.substring(0, 8)}...`}
              </span>
              <button 
                onClick={handleLogout}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px'
                }}
              >
              Logout
            </button>
          </div>
        ) : null}
      </div>
      
      {/* Navigation Menu */}
      {currentView !== 'dashboard' && (
        <div style={{marginBottom: '20px'}}>
          <button 
            onClick={() => setCurrentView('dashboard')}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '5px',
              cursor: 'pointer',
              marginRight: '10px'
            }}
          >
            ← Back to Dashboard
          </button>
        </div>
      )}

      {/* Dashboard View */}
      {currentView === 'dashboard' && (
        <div>
          <p style={{fontSize: '20px', textAlign: 'center', marginBottom: '30px', color: '#059669'}}>
            Welcome to BloodLink - Connecting donors with those in need
          </p>

      {/* Main Dashboard */}
      <div className="dashboard-grid">
        <div className="dashboard-card"
        style={{
          backgroundColor: '#fee2e2',
          border: '2px solid #dc2626'
        }}
        onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
        onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
        onClick={() => setCurrentView('search')}
        >
          <div style={{fontSize: '48px', marginBottom: '15px'}}>🔍</div>
          <h3 style={{color: '#dc2626', marginBottom: '10px'}}>Find Blood</h3>
          <p style={{color: '#666'}}>Search for blood donors in your area</p>
        </div>

        <div className="dashboard-card"
        style={{
          backgroundColor: '#dcfce7',
          border: '2px solid #16a34a'
        }}
        onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
        onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
        onClick={() => setCurrentView('donate')}
        >
          <div style={{fontSize: '48px', marginBottom: '15px'}}>🩸</div>
          <h3 style={{color: '#16a34a', marginBottom: '10px'}}>Donate Blood</h3>
          <p style={{color: '#666'}}>Register as a donor and save lives</p>
        </div>

        <div className="dashboard-card"
        style={{
          backgroundColor: '#dbeafe',
          border: '2px solid #2563eb'
        }}
        onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
        onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
        onClick={() => setCurrentView('profile')}
        >
          <div style={{fontSize: '48px', marginBottom: '15px'}}>👤</div>
          <h3 style={{color: '#2563eb', marginBottom: '10px'}}>My Profile</h3>
          <p style={{color: '#666'}}>Manage your donor information</p>
        </div>
      </div>

      {/* Status Information */}
      
        </div>
      )}

      {/* Current View Content */}
      {currentView === 'search' && <BloodSearchView />}
      {currentView === 'donate' && <DonateBloodView />}
      {currentView === 'profile' && <ProfileView user={user} />}
    </div>
    </>
  );
}

// Blood Search View Component
function BloodSearchView() {
  const [bloodType, setBloodType] = useState('');
  const [searchLocation, setSearchLocation] = useState({
    city: '',
    state: '',
    pinCode: ''
  });
  const [searchResults, setSearchResults] = useState([]);

  const handleLocationChange = (e) => {
    setSearchLocation({
      ...searchLocation,
      [e.target.name]: e.target.value
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    
    // Enhanced location matching logic
    const isExactMatch = (donor, search) => {
      return donor.city.toLowerCase() === search.city.toLowerCase() &&
             donor.state.toLowerCase() === search.state.toLowerCase() &&
             donor.pinCode === search.pinCode;
    };
    
    const isCityStateMatch = (donor, search) => {
      return donor.city.toLowerCase() === search.city.toLowerCase() &&
             donor.state.toLowerCase() === search.state.toLowerCase();
    };
    
    // Mock database of donors with locations
    const allDonors = [
      { 
        id: 1, 
        name: 'John Doe', 
        bloodType: 'A+',
        location: { city: 'Mumbai', state: 'Maharashtra', pinCode: '400001' },
        phone: '+91-98765-43210', 
        distance: '2.5 km',
        address: '123 Marine Drive, Mumbai, Maharashtra - 400001'
      },
      { 
        id: 2, 
        name: 'Jane Smith', 
        bloodType: 'B+',
        location: { city: 'Mumbai', state: 'Maharashtra', pinCode: '400002' },
        phone: '+91-98765-43211', 
        distance: '5.1 km',
        address: '456 Bandra West, Mumbai, Maharashtra - 400002'
      },
      { 
        id: 3, 
        name: 'Mike Johnson', 
        bloodType: 'O+',
        location: { city: 'Pune', state: 'Maharashtra', pinCode: '411001' },
        phone: '+91-98765-43212', 
        distance: '7.8 km',
        address: '789 FC Road, Pune, Maharashtra - 411001'
      },
      { 
        id: 4, 
        name: 'Sarah Wilson', 
        bloodType: 'AB+',
        location: { city: searchLocation.city, state: searchLocation.state, pinCode: searchLocation.pinCode },
        phone: '+91-98765-43213', 
        distance: '1.2 km',
        address: `321 Local Street, ${searchLocation.city}, ${searchLocation.state} - ${searchLocation.pinCode}`
      },
      { 
        id: 5, 
        name: 'David Brown', 
        bloodType: bloodType,
        location: { city: searchLocation.city, state: searchLocation.state, pinCode: '123456' },
        phone: '+91-98765-43214', 
        distance: '3.7 km',
        address: `654 Nearby Road, ${searchLocation.city}, ${searchLocation.state} - 123456`
      }
    ];
    
    // Filter donors based on blood type and location
    let filteredDonors = allDonors.filter(donor => {
      const bloodTypeMatch = donor.bloodType === bloodType;
      const exactLocationMatch = isExactMatch(donor.location, searchLocation);
      const cityStateMatch = isCityStateMatch(donor.location, searchLocation);
      
      return bloodTypeMatch && (exactLocationMatch || cityStateMatch);
    });
    
    // If no exact matches, show nearby donors with same blood type
    if (filteredDonors.length === 0) {
      filteredDonors = allDonors.filter(donor => donor.bloodType === bloodType);
    }
    
    // Sort by distance (closest first)
    filteredDonors.sort((a, b) => parseFloat(a.distance) - parseFloat(b.distance));
    
    setSearchResults(filteredDonors);
  };

  return (
    <div>
      <h2 style={{color: '#dc2626', marginBottom: '20px'}}>🔍 Find Blood Donors</h2>
      
      <form onSubmit={handleSearch} style={{marginBottom: '30px', padding: '20px', backgroundColor: '#f9fafb', borderRadius: '8px'}}>
        <div className="form-grid">
          <div>
            <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Blood Type Needed:</label>
            <select 
              value={bloodType} 
              onChange={(e) => setBloodType(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '5px'
              }}
            >
              <option value="">Select Blood Type</option>
              <option value="A+">A+</option>
              <option value="A-">A-</option>
              <option value="B+">B+</option>
              <option value="B-">B-</option>
              <option value="AB+">AB+</option>
              <option value="AB-">AB-</option>
              <option value="O+">O+</option>
              <option value="O-">O-</option>
            </select>
          </div>
          
          <div>
            <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>City:</label>
            <input
              type="text"
              name="city"
              placeholder="Enter city"
              value={searchLocation.city}
              onChange={handleLocationChange}
              required
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '5px'
              }}
            />
          </div>
          
          <div>
            <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>State:</label>
            <input
              type="text"
              name="state"
              placeholder="Enter state"
              value={searchLocation.state}
              onChange={handleLocationChange}
              required
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '5px'
              }}
            />
          </div>
          
          <div>
            <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Pin Code:</label>
            <input
              type="text"
              name="pinCode"
              placeholder="Enter pin code"
              value={searchLocation.pinCode}
              onChange={handleLocationChange}
              required
              pattern="[0-9]{6}"
              title="Please enter a valid 6-digit pin code"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '5px'
              }}
            />
          </div>
        </div>
        
        <button
          type="submit"
          style={{
            padding: '12px 30px',
            backgroundColor: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            fontSize: '16px',
            cursor: 'pointer'
          }}
        >
          Search Donors
        </button>
      </form>

      {searchResults.length > 0 && (
        <div>
          <h3 style={{marginBottom: '15px', color: '#374151'}}>
            Available Donors ({searchResults.length} found)
            {searchLocation.city && (
              <span style={{fontSize: '14px', color: '#6b7280', fontWeight: 'normal'}}>
                {' '} in {searchLocation.city}, {searchLocation.state} - {searchLocation.pinCode}
              </span>
            )}
          </h3>
          <div className="search-results-grid">
            {searchResults.map(donor => {
              const isExactLocationMatch = donor.location.city === searchLocation.city && 
                                         donor.location.state === searchLocation.state && 
                                         donor.location.pinCode === searchLocation.pinCode;
              const isCityStateMatch = donor.location.city === searchLocation.city && 
                                     donor.location.state === searchLocation.state;
              
              return (
                <div key={donor.id} className="donor-card" style={{
                  border: isExactLocationMatch ? '2px solid #16a34a' : isCityStateMatch ? '2px solid #f59e0b' : '1px solid #d1d5db',
                  backgroundColor: isExactLocationMatch ? '#f0fdf4' : isCityStateMatch ? '#fffbeb' : 'white'
                }}>
                  <div className="donor-info">
                    <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px'}}>
                      <h4 style={{margin: '0', color: '#dc2626'}}>{donor.name}</h4>
                      {isExactLocationMatch && (
                        <span style={{
                          padding: '2px 8px', 
                          backgroundColor: '#16a34a', 
                          color: 'white', 
                          borderRadius: '12px', 
                          fontSize: '12px'
                        }}>
                          Exact Match
                        </span>
                      )}
                      {isCityStateMatch && !isExactLocationMatch && (
                        <span style={{
                          padding: '2px 8px', 
                          backgroundColor: '#f59e0b', 
                          color: 'white', 
                          borderRadius: '12px', 
                          fontSize: '12px'
                        }}>
                          Same City
                        </span>
                      )}
                    </div>
                    <p style={{margin: '0 0 5px 0'}}>Blood Type: <strong>{donor.bloodType}</strong></p>
                    <p style={{margin: '0 0 5px 0'}}>Address: {donor.address}</p>
                    <p style={{margin: '0', fontSize: '14px', color: '#6b7280'}}>Distance: {donor.distance}</p>
                  </div>
                  <div className="donor-contact">
                    <button 
                      onClick={() => {
                        if (navigator.userAgent.includes('Mobile')) {
                          window.location.href = `tel:${donor.phone}`;
                        } else {
                          alert(`Contact ${donor.name} at ${donor.phone}\n\nFor mobile users: This will automatically dial the number.`);
                        }
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#059669',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        marginBottom: '5px',
                        display: 'block'
                      }}
                    >
                      Contact
                    </button>
                    <p style={{margin: '0', fontSize: '14px', color: '#6b7280'}}>{donor.phone}</p>
                  </div>
                  </div>
                
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Donate Blood View Component
function DonateBloodView({ user }) {
  const [formData, setFormData] = useState({
    fullName: '',
    bloodType: '',
    age: '',
    weight: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      pinCode: ''
    },
    medicalConditions: '',
    lastDonation: ''
  });

  const handleInputChange = (e) => {
    if (e.target.name.startsWith('address.')) {
      const addressField = e.target.name.split('.')[1];
      setFormData({
        ...formData,
        address: {
          ...formData.address,
          [addressField]: e.target.value
        }
      });
    } else {
      setFormData({
        ...formData,
        [e.target.name]: e.target.value
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const fullAddress = `${formData.address.street}, ${formData.address.city}, ${formData.address.state} - ${formData.address.pinCode}`;
    alert(`Thank you for registering as a blood donor!\n\nYour information has been saved:\nName: ${formData.fullName}\nBlood Type: ${formData.bloodType}\nAddress: ${fullAddress}`);
    console.log('Donor registration:', formData);
  };

  return (
    <div>
      <h2 style={{color: '#16a34a', marginBottom: '20px'}}>🩸 Become a Blood Donor</h2>
      
      <div style={{marginBottom: '20px', padding: '15px', backgroundColor: '#dcfce7', borderRadius: '8px', border: '1px solid #16a34a'}}>
        <h4 style={{margin: '0 0 10px 0', color: '#16a34a'}}>Why Donate Blood?</h4>
        <ul style={{margin: '0', paddingLeft: '20px'}}>
          <li>Save up to 3 lives with one donation</li>
          <li>Help patients with cancer, blood disorders, and trauma</li>
          <li>Free health screening included</li>
          <li>Join a community of life-savers</li>
        </ul>
      </div>

      <form onSubmit={handleSubmit} style={{padding: '20px', backgroundColor: '#f9fafb', borderRadius: '8px'}}>
        <div className="form-grid">
          <div>
            <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Full Name:</label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '5px'
              }}
            />
          </div>
          
          <div>
            <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Blood Type:</label>
            <select 
              name="bloodType"
              value={formData.bloodType} 
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '5px'
              }}
            >
              <option value="">Select Blood Type</option>
              <option value="A+">A+</option>
              <option value="A-">A-</option>
              <option value="B+">B+</option>
              <option value="B-">B-</option>
              <option value="AB+">AB+</option>
              <option value="AB-">AB-</option>
              <option value="O+">O+</option>
              <option value="O-">O-</option>
            </select>
          </div>
          
          <div>
            <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Age:</label>
            <input
              type="number"
              name="age"
              min="18"
              max="65"
              value={formData.age}
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '5px'
              }}
            />
          </div>
          
          <div>
            <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Weight (kg):</label>
            <input
              type="number"
              name="weight"
              min="50"
              value={formData.weight}
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '5px'
              }}
            />
          </div>
          
          <div>
            <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Phone Number:</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '5px'
              }}
            />
          </div>
          
          <div>
            <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Last Donation:</label>
            <input
              type="date"
              name="lastDonation"
              value={formData.lastDonation}
              onChange={handleInputChange}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '5px'
              }}
            />
          </div>
        </div>
        
        <div style={{marginBottom: '20px'}}>
          <h4 style={{marginBottom: '15px', color: '#374151'}}>Address Information:</h4>
          <div className="form-grid">
            <div>
              <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Street Address:</label>
              <input
                type="text"
                name="address.street"
                value={formData.address.street}
                onChange={handleInputChange}
                required
                placeholder="Enter street address"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '5px'
                }}
              />
            </div>
            
            <div>
              <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>City:</label>
              <input
                type="text"
                name="address.city"
                value={formData.address.city}
                onChange={handleInputChange}
                required
                placeholder="Enter city"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '5px'
                }}
              />
            </div>
            
            <div>
              <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>State:</label>
              <input
                type="text"
                name="address.state"
                value={formData.address.state}
                onChange={handleInputChange}
                required
                placeholder="Enter state"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '5px'
                }}
              />
            </div>
            
            <div>
              <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Pin Code:</label>
              <input
                type="text"
                name="address.pinCode"
                value={formData.address.pinCode}
                onChange={handleInputChange}
                required
                pattern="[0-9]{6}"
                title="Please enter a valid 6-digit pin code"
                placeholder="Enter 6-digit pin code"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '5px'
                }}
              />
            </div>
          </div>
        </div>
        
        <div style={{marginBottom: '20px'}}>
          <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Medical Conditions (if any):</label>
          <textarea
            name="medicalConditions"
            value={formData.medicalConditions}
            onChange={handleInputChange}
            rows="3"
            placeholder="List any medical conditions, medications, or health concerns..."
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #d1d5db',
              borderRadius: '5px',
              resize: 'vertical'
            }}
          />
        </div>
        
        <button
          type="submit"
          style={{
            padding: '15px 30px',
            backgroundColor: '#16a34a',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            fontSize: '16px',
            cursor: 'pointer'
          }}
        >
          Register as Donor
        </button>
      </form>
    </div>
  );
}

// Profile View Component
function ProfileView({ user }) {
  return (
    <div>
      <h2 style={{color: '#2563eb', marginBottom: '20px'}}>👤 My Profile</h2>
      
      <div style={{padding: '20px', backgroundColor: '#f9fafb', borderRadius: '8px'}}>
        <div style={{marginBottom: '20px'}}>
          <h4 style={{marginBottom: '10px', color: '#374151'}}>Account Information</h4>
          <p><strong>Email:</strong> {user?.email || 'Anonymous User'}</p>
          <p><strong>User ID:</strong> {user?.uid.substring(0, 8)}...</p>
          <p><strong>Account Type:</strong> {user?.email ? 'Registered User' : 'Guest User'}</p>
        </div>
        
        {!user?.email && (
          <div style={{
            padding: '15px',
            backgroundColor: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h4 style={{margin: '0 0 10px 0', color: '#92400e'}}>Complete Your Registration</h4>
            <p style={{margin: '0', color: '#92400e'}}>
              Create an account to save your donor information and get notified when your blood type is needed.
            </p>
          </div>
        )}
        
        <div>
          <h4 style={{marginBottom: '10px', color: '#374151'}}>Donation History</h4>
          <p style={{color: '#6b7280', fontStyle: 'italic'}}>No donation history available yet.</p>
        </div>
        
        <div style={{marginTop: '20px'}}>
          <h4 style={{marginBottom: '10px', color: '#374151'}}>Settings</h4>
          <div className="button-group">
            <button 
              onClick={() => {
                const newName = prompt('Enter your new name:');
                if (newName) {
                  alert(`Profile updated! New name: ${newName}\n\nNote: In a full app, this would update your Firebase profile.`);
                }
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#2bd650ff',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              Edit Profile
            </button>
            <button 
              onClick={() => {
                const notifications = window.confirm('Do you want to receive email notifications when your blood type is needed?');
                if (notifications) {
                  alert('✅ Email notifications enabled!\n\nYou will receive alerts when donors with your blood type are needed.');
                } else {
                  alert('📧 Email notifications disabled.\n\nYou can re-enable them anytime from your profile settings.');
                }
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#16a34a',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              Notification Settings
            </button>
            <button 
              onClick={() => {
                const options = ['Public - Visible to all users', 'Limited - Visible to verified donors only', 'Private - Only visible when you initiate contact'];
                const choice = prompt(`Choose your privacy level:\n\n1. ${options[0]}\n2. ${options[1]}\n3. ${options[2]}\n\nEnter 1, 2, or 3:`);
                
                if (choice >= 1 && choice <= 3) {
                  alert(`🔒 Privacy settings updated!\n\nYour profile is now: ${options[choice-1]}\n\nNote: In a full app, this would update your Firebase security rules.`);
                } else if (choice !== null) {
                  alert('Please enter a valid option (1, 2, or 3)');
                }
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              Privacy Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Login Form Component
function LoginForm({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onLogin(email, password);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{marginBottom: '15px'}}>
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          style={{
            width: '100%',
            padding: '12px',
            border: '1px solid #d1d5db',
            borderRadius: '5px',
            fontSize: '14px'
          }}
        />
      </div>
      <div style={{marginBottom: '15px'}}>
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          style={{
            width: '100%',
            padding: '12px',
            border: '1px solid #d1d5db',
            borderRadius: '5px',
            fontSize: '14px'
          }}
        />
      </div>
      <button
        type="submit"
        style={{
          width: '100%',
          padding: '12px',
          backgroundColor: '#dc2626',
          color: 'white',
          border: 'none',
          borderRadius: '5px',
          fontSize: '16px',
          cursor: 'pointer'
        }}
      >
        Login
      </button>
    </form>
  );
}

// Signup Form Component
function SignupForm({ onSignup }) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    address: {
      street: '',
      city: '',
      state: '',
      pinCode: ''
    },
    bloodType: '',
    contact: ''
  });

  const handleInputChange = (e) => {
    if (e.target.name.startsWith('address.')) {
      const addressField = e.target.name.split('.')[1];
      setFormData({
        ...formData,
        address: {
          ...formData.address,
          [addressField]: e.target.value
        }
      });
    } else {
      setFormData({
        ...formData,
        [e.target.name]: e.target.value
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Create full address string for storage
    const fullAddress = `${formData.address.street}, ${formData.address.city}, ${formData.address.state} - ${formData.address.pinCode}`;
    const signupData = {
      ...formData,
      addressString: fullAddress,
      addressComponents: formData.address
    };
    onSignup(signupData);
  };

  return (
    <div style={{maxHeight: '400px', overflowY: 'auto'}}>
      <form onSubmit={handleSubmit}>
        <div style={{marginBottom: '15px'}}>
          <input
            type="text"
            name="fullName"
            placeholder="Full Name"
            value={formData.fullName}
            onChange={handleInputChange}
            required
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #d1d5db',
              borderRadius: '5px',
              fontSize: '14px'
            }}
          />
        </div>
        <div style={{marginBottom: '15px'}}>
          <input
            type="email"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleInputChange}
            required
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #d1d5db',
              borderRadius: '5px',
              fontSize: '14px'
            }}
          />
        </div>
        <div style={{marginBottom: '15px'}}>
          <input
            type="password"
            name="password"
            placeholder="Password (min. 6 characters)"
            value={formData.password}
            onChange={handleInputChange}
            required
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #d1d5db',
              borderRadius: '5px',
              fontSize: '14px'
            }}
          />
        </div>
        <div style={{marginBottom: '15px'}}>
          <select
            name="bloodType"
            value={formData.bloodType}
            onChange={handleInputChange}
            required
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #d1d5db',
              borderRadius: '5px',
              fontSize: '14px'
            }}
          >
            <option value="">Select Blood Type</option>
            <option value="A+">A+</option>
            <option value="A-">A-</option>
            <option value="B+">B+</option>
            <option value="B-">B-</option>
            <option value="AB+">AB+</option>
            <option value="AB-">AB-</option>
            <option value="O+">O+</option>
            <option value="O-">O-</option>
          </select>
        </div>
        <div style={{marginBottom: '15px'}}>
          <input
            type="tel"
            name="contact"
            placeholder="Contact Number"
            value={formData.contact}
            onChange={handleInputChange}
            required
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #d1d5db',
              borderRadius: '5px',
              fontSize: '14px'
            }}
          />
        </div>
        
        <div style={{marginBottom: '15px'}}>
          <h5 style={{margin: '0 0 10px 0', color: '#374151'}}>Address Details:</h5>
          <div style={{display: 'grid', gap: '10px'}}>
            <input
              type="text"
              name="address.street"
              placeholder="Street Address"
              value={formData.address.street}
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '5px',
                fontSize: '14px'
              }}
            />
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px'}}>
              <input
                type="text"
                name="address.city"
                placeholder="City"
                value={formData.address.city}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '5px',
                  fontSize: '14px'
                }}
              />
              <input
                type="text"
                name="address.state"
                placeholder="State"
                value={formData.address.state}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '5px',
                  fontSize: '14px'
                }}
              />
            </div>
            <input
              type="text"
              name="address.pinCode"
              placeholder="Pin Code (6 digits)"
              value={formData.address.pinCode}
              onChange={handleInputChange}
              required
              pattern="[0-9]{6}"
              title="Please enter a valid 6-digit pin code"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '5px',
                fontSize: '14px'
              }}
            />
          </div>
        </div>
        <button
          type="submit"
          style={{
            width: '100%',
            padding: '12px',
            backgroundColor: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            fontSize: '16px',
            cursor: 'pointer'
          }}
        >
          Sign Up
        </button>
      </form>
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <BloodLinkApp />
    </ErrorBoundary>
  </React.StrictMode>
);